/**
 * @file validate.js
 * @description Collection of reusable functions, classes, or methods that are used for validation
 */

import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { FileTypeSizeLimits, ImportFileTypeSizeLimits } from './data/constants';

/**
 * Validates a specified amount is a money value
 * @param {decimal} amount - The amount that should be validated
 * @param {string} fieldName - The field name that will be used in the error message
 * @returns {string} - The validation error message or null if it is valid
 */
export const isValidMoney = (inputAmount, fieldName) => {
    if (inputAmount && isNaN(inputAmount))
        return `${fieldName} must be numeric`;

    var amount = Number(inputAmount);
    // amount is in the valid range
    if (amount <= -1000000000 || amount >= 1000000000)
        return (
            fieldName + ' must be between -999,999,999.99 and 999,999,999.99'
        );

    var decimalValue = (amount + '').split('.')[1];

    // only allow 2 decimal places
    if (decimalValue && decimalValue.length > 2)
        return fieldName + ' can have a maximum of 2 decimal places';

    return null;
};

/**
 * Validates whether a given zip code matches the U.S. postal code format (5 digits or 5+4 digits).
 * @param {string} zip - The zip code to validate
 * @returns {boolean} True if the zip code is valid, false otherwise
 * @example
 * isValidZipCode('12345')    // returns true
 * isValidZipCode('12345-6789') // returns true
 * isValidZipCode('1234')     // returns false
 */
export const isValidZipCode = zip => {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip);
};

/**
 * Validates the format of an email address.
 * @param {string} emailAddress - The email address to validate.
 * @returns {bool} - An error message if invalid, otherwise an empty string.
 */
export const isValidEmailAddress = emailAddress => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailAddress);
};

/**
 * Validates the format of a phone number
 * @param {string} phoneNumber - The phone number to validate.
 * @returns {bool}
 */
export const isValidPhoneNumber = phoneNumber => {
    if (!phoneNumber) return false;
    let trimmedNumber = phoneNumber.trim();
    if (!trimmedNumber.startsWith('+')) {
        if (/^\d{10}$/.test(trimmedNumber)) {
            trimmedNumber = `+1${trimmedNumber}`;
        } else if (trimmedNumber.startsWith('1')) {
            trimmedNumber = `+${trimmedNumber}`;
        }
    }
    return isPossiblePhoneNumber(trimmedNumber);
};

/**
 * Validates and formats a URL
 * @param {string} url - The URL to validate
 * @returns {Object} Object containing validation results
 * @property {boolean} isValid - Indicates if the URL is valid
 * @property {string} [formattedUrl] - The formatted URL with https:// if needed
 * @property {string} error - Error message if URL is invalid
 * @example
 * validateUrl('example.com') // returns { isValid: true, formattedUrl: 'https://example.com', error: '' }
 * validateUrl('') // returns { isValid: false, error: 'URL is required' }
 * validateUrl('invalid url') // returns { isValid: false, error: 'Please enter a valid URL (e.g., https://example.com)' }
 */
export const validateUrl = url => {
    if (!url) {
        return {
            isValid: false,
            error: 'URL is required',
        };
    }

    const urlRegex = new RegExp(
        '^(https?:\\/\\/)?' +
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
            '((\\d{1,3}\\.){3}\\d{1,3}))' +
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
            '(\\?[;&a-z\\d%_.~+=-]*)?' +
            '(\\#[-a-z\\d_]*)?$',
        'i',
    );

    if (!urlRegex.test(url)) {
        return {
            isValid: false,
            error: 'Please enter a valid URL (e.g., https://example.com)',
        };
    }

    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return {
            isValid: true,
            formattedUrl: `https://${url}`,
            error: '',
        };
    }

    return {
        isValid: true,
        formattedUrl: url,
        error: '',
    };
};

/**
 * Validates the type and size of a file
 * @param {File} file - The file to validate.
 * @returns {string} - The validation error message or null if it is valid
 */
export const isValidFileTypeAndSize = file => {
    const mimeType = file.type.toLowerCase();
    const sizeLimit = FileTypeSizeLimits[mimeType];

    if (!sizeLimit)
        return 'File is not of the correct type. Valid types include Excel, Word, PowerPoint, csv and text files';

    return null; //No errors
};

/**
 * Validates the type and size of an import file
 * @param {File} file - The file to validate.
 * @returns {string} - The validation error message or null if it is valid
 */
export const isValidImportFileTypeAndSize = file => {
    const mimeType = file.type.toLowerCase();
    const sizeLimit = ImportFileTypeSizeLimits[mimeType];

    if (!sizeLimit)
        return 'File is not of the correct type. Valid types include Excel, csv and text files';

    return null; //No errors
};

/**
 * Validates the mobile icon file type, size and dimensions
 * @param {File} file - The file to validate.
 * @returns {Promise<string>} - The validation error message or null if it is valid
 */
export const isValidMobileIcon = file => {
    return new Promise(resolve => {
        if (file.type.toLowerCase() !== 'image/png') {
            resolve('Mobile icon must be a PNG file');
            return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            if (img.width !== img.height) {
                resolve(
                    'Mobile icon must be square (width and height must be equal)',
                );
                return;
            }

            if (img.width < 512 || img.height < 512) {
                resolve('Mobile icon must be at least 512x512 pixels');
                return;
            }

            resolve(null);
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve('Invalid image file');
        };

        img.src = url;
    });
};
